import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Extension as ExtensionIcon,
  Info as InfoIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { BaseFormComponent } from '../types';
import { getFieldFamilyLabel, getFieldFamilyColor } from '../helpers';
import { getFormComponentDisplay, translateFormPropertyType } from '../../processDisplay';

interface ComponentDialogProps {
  open: boolean;
  component: BaseFormComponent | null;
  onClose: () => void;
  onEdit: (component: BaseFormComponent) => void;
  onDelete: (component: BaseFormComponent) => void;
}

const ComponentDialog: React.FC<ComponentDialogProps> = ({ open, component, onClose, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  if (!component) return null;
  const display = getFormComponentDisplay(t, component);

  const headerColor = getFieldFamilyColor(theme, component.category);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      {/* Colored header banner */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${headerColor}, ${headerColor}dd)`,
          color: theme.palette.getContrastText(headerColor),
          position: 'relative',
        }}
      >
        <IconButton aria-label={t('common:actions.close')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'inherit',
            bgcolor: 'rgba(255,255,255,0.15)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'inherit',
              '& svg': { fontSize: 32 },
            }}
          >
            {component.icon ? React.cloneElement(component.icon, { fontSize: 'inherit' as any }) : <ExtensionIcon />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>{display.label}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
              <Chip
                label={getFieldFamilyLabel(t, component.category)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'inherit',
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
              {component.isSystem && (
                <Chip
                  label={t('processes:formComponents.dialog.system')}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'inherit',
                    fontWeight: 600,
                    height: 22,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              <Chip
                label={component.configurable ? t('processes:formComponents.configurable') : t('processes:liveDisplay.ui.readOnly')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'inherit',
                  fontWeight: 500,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ py: 3 }}>
        {display.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {display.description}
          </Typography>
        )}

        {component.isSystem && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            {t('processes:liveDisplay.ui.systemComponentInfo')}
          </Alert>
        )}

        {/* Identifier row */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">{t('processes:formComponents.dialog.identifier')}</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', mt: 0.25 }}>{component.name}</Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">{t('processes:formComponents.dialog.usageCount')}</Typography>
            <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mt: 0.25 }}>
              {t('processes:liveDisplay.ui.usageTimes', { count: component.usageCount })}
            </Typography>
          </Box>
        </Stack>

        {/* Properties */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon fontSize="small" />
          {/* "Field properties", not "Configurable properties". The heading counted the
              properties the TYPE exposes while the body below gated on
              `component.configurable` — which the seeder sets to False for every system
              component and actively repairs back to False. So every system component
              announced "Configurable properties (3)" and then denied the three existed.
              One word, two meanings; the heading was using the wrong one.

              Whether an ADMIN may edit this catalog entry is a different fact, and it is
              already stated by the Configurable / Read-only chip above (2026-08-24). */}
          {t('processes:liveDisplay.ui.fieldProperties', { count: display.properties.length })}
        </Typography>
        {display.properties.length > 0 ? (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            {display.properties.map((prop, i) => (
              <Box
                key={prop.name}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderBottom: i < display.properties.length - 1 ? '1px solid' : 0,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: prop.description ? 0.25 : 0 }}>
                    <Typography variant="body2" fontWeight={600}>{prop.label}</Typography>
                    <Chip
                      label={translateFormPropertyType(t, prop.type)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'monospace' }}
                    />
                    {prop.required && (
                      <Chip label={t('processes:formComponents.dialog.required')} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                  {prop.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {prop.description}
                    </Typography>
                  )}
                  {prop.defaultValue !== undefined && prop.defaultValue !== null && prop.defaultValue !== '' && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, fontFamily: 'monospace' }}>
                      {t('processes:liveDisplay.ui.defaultValue')}: {String(prop.defaultValue)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {/* One empty state, because there is one fact: this type exposes nothing.
                The old pair branched on `configurable` and the read-only arm read
                "This component is not configurable." — a denial rather than an answer. */}
            {t('processes:componentDialog.noProperties')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        {!component.isSystem && (
          <>
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(component)} sx={{ mr: 'auto' }}>
              {t('common:actions.delete')}
            </Button>
            <Button startIcon={<EditIcon />} onClick={() => onEdit(component)}>
              {t('common:actions.edit')}
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="contained">{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComponentDialog;
